import React, { createContext, useContext, useState } from "react";
import { useParams } from "react-router-dom";

import { apiClient } from "../api/ApiClient";
import { extractAuthorizations } from "../helpers/authorizations";

type CustomizationGroup = "enhancement" | "ai" | "schema" | "ai_instructions";

type CustomizationKind = "titles" | "descriptions" | "schemas" | "summary";

interface AuthorizationContextType {
  authorizations: string[];
  isLoading: boolean;
  refetchAuthorizations: (
    pageId: number,
    group: CustomizationGroup,
  ) => Promise<void>;
  updateAuthorizations: (authorizations: string[]) => void;
  canGenerate: (group: CustomizationGroup) => boolean;
  canGenerateForKind: (kind: CustomizationKind) => boolean;
}

const AutomationAuthorizationContext = createContext<
  AuthorizationContextType | undefined
>(undefined);

export const useAutomationAuthorizations = (): AuthorizationContextType => {
  const context = useContext(AutomationAuthorizationContext);
  if (!context) {
    throw new Error(
      "useAutomationAuthorizations must be used within an AutomationAuthorizationProvider",
    );
  }

  return context;
};

interface AutomationAuthorizationProviderProps {
  children: React.ReactNode;
}

export const AutomationAuthorizationProvider: React.FC<
  AutomationAuthorizationProviderProps
> = ({ children }) => {
  const [authorizations, setAuthorizations] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { id } = useParams<{ id: string }>();

  const fetchAuthorizations = async (
    pageId: number,
    group: CustomizationGroup,
  ) => {
    if (!id || !pageId) return;

    setIsLoading(true);
    try {
      const headers = {
        "Authorize-Collection-Actions": "update",
        "Authorize-Member-Actions":
          "generate_title,generate_description,generate_schema,generate_ai_instructions,destroy",
      };
      const response =
        group === "ai_instructions"
          ? await apiClient.sites.pagesWidgetCustomizationsAiInstructionsDetail(
              Number(id),
              pageId,
              undefined,
              { headers },
            )
          : await apiClient.sites.pagesWidgetCustomizationsDetail(
              Number(id),
              pageId,
              group,
              undefined,
              { headers },
            );

      const auth = extractAuthorizations(response?.data);
      const memberAuths = auth?.member || [];
      const flattenedAuthorizations = memberAuths.flat();
      setAuthorizations(flattenedAuthorizations);
    } catch (error) {
      console.error("Failed to fetch authorizations:", error);
      setAuthorizations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const refetchAuthorizations = async (
    pageId: number,
    group: CustomizationGroup,
  ) => {
    await fetchAuthorizations(pageId, group);
  };

  const canGenerate = (group: string): boolean => {
    if (authorizations.length === 0) return false;

    const requiredPermissions: Record<string, string[]> = {
      ai: ["generate_title", "generate_description"],
      schema: ["generate_schema"],
      ai_instructions: ["generate_ai_instructions"],
      enhancement: ["generate_title", "generate_description"],
    };

    const permissions = requiredPermissions[group] || [];

    return permissions.some((permission) =>
      authorizations.includes(permission),
    );
  };

  const canGenerateForKind = (kind: CustomizationKind): boolean => {
    if (authorizations.length === 0) return false;

    const requiredPermissions: Record<string, string> = {
      titles: "generate_title",
      descriptions: "generate_description",
      schemas: "generate_schema",
      summary: "generate_ai_instructions",
    };

    const requiredPermission = requiredPermissions[kind];

    return authorizations.includes(requiredPermission);
  };

  const updateAuthorizations = (newAuthorizations: string[]) => {
    setAuthorizations(newAuthorizations);
  };

  const value: AuthorizationContextType = {
    authorizations,
    isLoading,
    refetchAuthorizations,
    updateAuthorizations,
    canGenerate,
    canGenerateForKind,
  };

  return (
    <AutomationAuthorizationContext.Provider value={value}>
      {children}
    </AutomationAuthorizationContext.Provider>
  );
};
